import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Col, FormLabel, Row } from "react-bootstrap";
import ListPage from "../../../layouts/page/ListPage";
import PageTitle from "../../../layouts/PageTitle";
import FilterAccordion from "../../../components/Common/Filter/FilterAccordion";
import ListMetricCard, { ListMetricCards } from "../../../components/Common/ListParts/ListMetricCard";
import CustomSelect from "../../../components/Forms/Element/CustomSelect";
import InputDate from "../../../components/Forms/Element/InputDate";
import { ApiError } from "../../../components/Common/ApiError";
import { warnAlert } from "../../../components/alert/defaultAlert";
import { Columns } from "./Columns";
import MenuConfig from "../../../../constants/MenuConfig";
import Api from "../../../../constants/Api";
import axiosInstance from "../../../../services/AxiosInstance";
import { SearchOperation } from "../../../../utils/ApiUtils";
import { formatDateToYYYYMMDD } from "../../../../utils/DateUtils";

/**
 * Feriados e horários especiais das filiais.
 *
 * Fica em Agendamentos, e não no cadastro de cada filial, porque o feriado costuma valer para a
 * rede inteira: a data é cadastrada uma vez, já com todas as filiais marcadas.
 */

/** A tela abre de hoje a um ano à frente: o que interessa são os feriados que ainda vêm. */
const defaultPeriod = () => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setFullYear(start.getFullYear() + 1);
    return { start, end, branch: null };
};

/** Traduz os campos da tela para os filtros que a grade manda ao servidor. */
const buildFilters = ({ start, end, branch }) => [
    { field: "specialDate", value: formatDateToYYYYMMDD(start), operation: SearchOperation.GREATER_THAN_EQUAL },
    { field: "specialDate", value: formatDateToYYYYMMDD(end), operation: SearchOperation.LESS_THAN_EQUAL },
    ...(branch ? [{ field: "branches.id", value: branch.value, operation: SearchOperation.EQUAL }] : [])
];

const SpecialDayList = () => {
    const { t } = useTranslation();

    const [startDate, setStartDate] = useState(() => defaultPeriod().start);
    const [endDate, setEndDate] = useState(() => defaultPeriod().end);
    const [branchFilter, setBranchFilter] = useState(null);
    const [branchOptions, setBranchOptions] = useState([]);

    // Recorte aplicado: a grade e os cards usam o mesmo, e só mudam ao pesquisar.
    const [applied, setApplied] = useState(defaultPeriod);
    const [filters, setFilters] = useState(() => buildFilters(defaultPeriod()));

    const [metrics, setMetrics] = useState({ total: 0, closed: 0, special: 0, affected: 0 });
    const [loadingMetrics, setLoadingMetrics] = useState(false);

    // As filiais do próprio usuário: operador restrito à sua unidade só vê a dela.
    useEffect(() => {
        axiosInstance.get(`${Api.main.branch}/user`)
            .then((response) => setBranchOptions((Array.isArray(response.data) ? response.data : [])
                .filter((branch) => branch.status === "ACTIVE")
                .map((branch) => ({ value: branch.id, label: branch.name }))))
            .catch(() => setBranchOptions([]));
    }, []);

    // Os cards somam o período inteiro numa consulta própria: a grade pagina e não devolve os
    // registros para a tela.
    const loadMetrics = useCallback(() => {
        setLoadingMetrics(true);
        axiosInstance.get(Api.appointment.special_day, {
            params: {
                startDate: formatDateToYYYYMMDD(applied.start),
                endDate: formatDateToYYYYMMDD(applied.end),
                ...(applied.branch ? { branchId: applied.branch.value } : {})
            }
        })
            .then(({ data }) => {
                const rows = Array.isArray(data) ? data : [];
                setMetrics({
                    total: rows.length,
                    closed: rows.filter((day) => !day.isOpen).length,
                    special: rows.filter((day) => day.isOpen).length,
                    affected: rows.reduce((total, day) => total + (day.affectedAppointments || []).length, 0)
                });
            })
            .catch(ApiError)
            .finally(() => setLoadingMetrics(false));
    }, [applied]);

    useEffect(() => {
        loadMetrics();
    }, [loadMetrics]);

    const handleSearch = () => {
        if (!startDate || !endDate) {
            warnAlert(t("period_filter_requires_both_dates"));
            return;
        }
        const next = { start: startDate, end: endDate, branch: branchFilter };
        setApplied(next);
        setFilters(buildFilters(next));
    };

    // Limpar volta ao período de abertura, e não à tela sem período.
    const handleClearFilters = () => {
        const period = defaultPeriod();
        setStartDate(period.start);
        setEndDate(period.end);
        setBranchFilter(null);
        setApplied(period);
        setFilters(buildFilters(period));
    };

    return (
        <>
            <PageTitle activeMenu={t("special_days")} motherMenu={t("appointments")} />

            <FilterAccordion title="filters" onSearch={handleSearch} onClear={handleClearFilters} centerButtons={false}>
                <Row className="g-2 align-items-end">
                    <Col lg={3} md={6}>
                        <FormLabel>{t("start")}</FormLabel>
                        <InputDate selected={startDate} onChange={setStartDate} />
                    </Col>
                    <Col lg={3} md={6}>
                        <FormLabel>{t("end")}</FormLabel>
                        <InputDate selected={endDate} onChange={setEndDate} />
                    </Col>
                    <Col lg={4} md={6}>
                        <FormLabel>{t("branch")}</FormLabel>
                        <CustomSelect
                            value={branchFilter}
                            options={branchOptions}
                            onChange={setBranchFilter}
                            placeholder={t("all_branches")}
                            isClearable
                        />
                    </Col>
                </Row>
            </FilterAccordion>

            <ListMetricCards columns={4}>
                <ListMetricCard accent="primary" label="Datas no período" value={metrics.total}
                    icon="fas fa-calendar-day" loading={loadingMetrics} />
                <ListMetricCard accent="warning" label="Dias fechados" value={metrics.closed}
                    icon="fas fa-store-slash" loading={loadingMetrics} />
                <ListMetricCard accent="info" label="Horário especial" value={metrics.special}
                    icon="fas fa-clock" loading={loadingMetrics} />
                <ListMetricCard accent="danger" label="Agendamentos a remarcar" value={metrics.affected}
                    icon="fas fa-triangle-exclamation" loading={loadingMetrics}
                    caption="Marcados antes do cadastro" />
            </ListMetricCards>

            {/* Aviso no padrão do sistema: o tema desenha o fundo e a borda do .alert nos dois modos. */}
            <Alert variant="info" className="py-2">
                <i className="fa-solid fa-circle-info me-2"></i>
                {t("special_days_description")}
            </Alert>

            <ListPage
                showPageTitle={false}
                activeMenu={t("special_days")}
                motherMenu={t("appointments")}
                path={Api.appointment.special_day}
                route={MenuConfig.appointment.special_day.route}
                columns={Columns}
                filters={filters}
                // Não há situação ativa ou inativa: remover devolve o dia ao horário semanal.
                hideInactiveToggle
                onDelete={loadMetrics}
            />
        </>
    );
};

export default SpecialDayList;
